import { useCallback, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import Cropper from "react-easy-crop";
import Modal from "@/components/Modal";

async function _cropToBlob(imageUrl, area) {
  const img = new Image();
  img.src = imageUrl;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const canvas = document.createElement("canvas");
  const size = 512;
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

export default function PhotoUpload({ value, onChange, shape = "square", testId = "photo-upload" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [rawUrl, setRawUrl] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);

  const isCircle = shape === "circle";
  const rounded = isCircle ? "rounded-full" : "rounded-3xl";
  const size = isCircle ? "w-32 h-32" : "w-full aspect-video";
  const cropShape = isCircle ? "round" : "rect";
  const cropAspect = isCircle ? 1 : 16 / 9;

  const pick = () => inputRef.current?.click();
  const onCropComplete = useCallback((_, areaPx) => setPixels(areaPx), []);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (isCircle) {
      // Show crop modal for profile pics
      setRawFile(f);
      setRawUrl(URL.createObjectURL(f));
      setCrop({ x: 0, y: 0 }); setZoom(1);
    } else {
      uploadRaw(f);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const uploadRaw = async (blob, filename = "upload.jpg") => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Foto terunggah");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal unggah");
    } finally { setBusy(false); }
  };

  const confirmCrop = async () => {
    if (!rawUrl || !pixels) return;
    setBusy(true);
    try {
      const blob = await _cropToBlob(rawUrl, pixels);
      URL.revokeObjectURL(rawUrl);
      setRawUrl(null); setRawFile(null); setPixels(null);
      await uploadRaw(blob, "profile.jpg");
    } catch (err) { toast.error("Gagal crop foto"); }
    finally { setBusy(false); }
  };

  const cancelCrop = () => {
    if (rawUrl) URL.revokeObjectURL(rawUrl);
    setRawUrl(null); setRawFile(null); setPixels(null);
  };

  return (
    <>
      <div className="flex flex-col items-start gap-3" data-testid={testId}>
        <div className={`relative ${size} ${rounded} overflow-hidden border-2 border-dashed border-bizma-border bg-bizma-muted grid place-items-center`}>
          {value ? <img src={fileUrl(value)} alt="" className={`w-full h-full object-cover ${rounded}`} /> : <Camera className="text-bizma-subtext" size={28}/>}
          {busy && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Loader2 className="animate-spin text-white"/></div>}
        </div>
        <button type="button" onClick={pick} className="btn-outline text-sm py-2 px-4" data-testid={`${testId}-btn`}>{value?"Ganti Foto":"Unggah Foto"}</button>
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
      </div>

      {rawUrl && (
        <Modal
          open={!!rawUrl}
          onClose={cancelCrop}
          title="Atur Foto"
          size="md"
          testId="crop-modal"
          footer={
            <>
              <button onClick={cancelCrop} className="btn-outline flex-1" data-testid="crop-cancel">Batal</button>
              <button onClick={confirmCrop} disabled={busy || !pixels} className="btn-primary flex-1" data-testid="crop-confirm">{busy?"Menyimpan...":"Gunakan Foto"}</button>
            </>
          }
        >
          <div className="relative w-full h-64 sm:h-72 bg-bizma-muted rounded-2xl overflow-hidden" data-testid="crop-canvas">
            <Cropper image={rawUrl} crop={crop} zoom={zoom} aspect={cropAspect} cropShape={cropShape}
              showGrid={!isCircle} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          </div>
          <div className="mt-4">
            <label className="text-xs text-bizma-subtext">Zoom</label>
            <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e)=>setZoom(parseFloat(e.target.value))} className="w-full accent-bizma-terracotta" data-testid="crop-zoom" />
          </div>
        </Modal>
      )}
    </>
  );
}
